import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useContext, useContextSelector } from 'use-context-selector'

import { PromoPageStateContext } from 'states/PromoPageState/PromoPageStateProvider'
import { SectionState } from 'states/PromoPageState/state'
import { UserStateContext } from 'states/UserState/UserStateProvider'
import { ConferenceType } from 'sections/PromoSection/pages/ConferencesPage/types'
import { createDate } from 'utils/helpers/date/createDate'
import { createMonth } from 'utils/helpers/date/createMonth'
import { getMonthNames } from 'utils/helpers/date/getMonthNames'
import { getMonthNumberOfDays } from 'utils/helpers/date/getMonthNumberOfDays'
import { getWeekDaysNames } from 'utils/helpers/date/getWeekDaysNames'
import { scrollToIdWithOffset } from 'utils/helpers/scrollToIdWithOffset'
import { SCROLL_TO_ID } from 'shared/constants/values'
import { ConferenceData, SectionName } from 'types/types'

import 'dayjs/locale/ru'

type DayType = {
  conferences: ConferenceType[]
  date: Date
  day: string
  dayNumber: number | string
  dayNumberInWeek: number
  dayShort: string
  monthIndex: number
  monthName: string
  monthNumber: number
  monthShort: string
  timestamp: number
  week: number
  year: number
  yearShort: string
}

const getYearsInterval = (year: number) => {
  const startYear = Math.floor(year / 10) * 10
  return [...Array(10)].map((_, index) => startYear + index)
}

export const useCalendar = (date: Date = new Date(), locale: string = 'RU') => {
  const state = useContextSelector(PromoPageStateContext, (context) =>
    context.state.sections.find((section) => section.type === SectionName.conferences),
  ) as SectionState

  const ctx = useContext(UserStateContext)

  const [selectedDate, setSelectedDate] = useState(createDate({ date }))
  const [selectedMonth, setSelectedMonth] = useState(
    createMonth({ date: new Date(selectedDate.year, selectedDate.monthIndex), locale }),
  )
  const [selectedYear, setSelectedYear] = useState(selectedDate.year)
  const [selectedYearsInterval, setSelectedYearsInterval] = useState(getYearsInterval(selectedDate.year))
  const [mode, setMode] = useState<'days' | 'months' | 'years'>('days')

  const monthNames = useMemo(() => getMonthNames(locale), [])
  const weekDayNames = useMemo(() => getWeekDaysNames(locale), [])

  const [selectedWeek, setSelectedWeek] = useState({ start: '', end: '' })
  const [viewMode, setViewMode] = useState(ctx.state.adminRole ? 'month' : 'week')

  const updateWeek = (newDate: Date) => {
    const start = dayjs(newDate).weekday(0)
    const end = dayjs(newDate).weekday(6)
    const formattedStart = start.format('DD MMMM')
    const formattedEnd = end.format('DD MMMM')
    setSelectedWeek({ start: formattedStart, end: formattedEnd })
  }

  useEffect(() => {
    updateWeek(selectedDate.date)
  }, [selectedDate])

  const updateMonthIndexIfNeeded = (date: Date, selectedMonthIndex: number, setMonthIndex: (index: number) => void) => {
    const newMonthIndex = dayjs(date).month()
    if (newMonthIndex !== selectedMonthIndex) {
      setMonthIndex(newMonthIndex)
    }
  }

  const goToPreviousWeekOrMonth = () => {
    if (viewMode === 'week') {
      const newStartOfWeek = dayjs(selectedDate.date).subtract(1, 'week').toDate()
      const updatedDate = createDate({ date: newStartOfWeek })
      setSelectedDate(updatedDate)
      updateWeek(newStartOfWeek)
      updateMonthIndexIfNeeded(newStartOfWeek, selectedMonth.monthIndex, setMonthIndex)
    } else {
      const newStartOfMonth = dayjs(selectedDate.date).subtract(1, 'month').startOf('month').toDate()
      const updatedDate = createDate({ date: newStartOfMonth })
      setSelectedDate(updatedDate)
      setMonthIndex(selectedMonth.monthIndex - 1)
    }
  }

  const goToNextWeekOrMonth = () => {
    if (viewMode === 'week') {
      const newStartOfWeek = dayjs(selectedDate.date).add(1, 'week').toDate()
      const updatedDate = createDate({ date: newStartOfWeek })
      setSelectedDate(updatedDate)
      updateWeek(newStartOfWeek)
      updateMonthIndexIfNeeded(newStartOfWeek, selectedMonth.monthIndex, setMonthIndex)
    } else {
      const newStartOfMonth = dayjs(selectedDate.date).add(1, 'month').startOf('month').toDate()
      const updatedDate = createDate({ date: newStartOfMonth })
      setSelectedDate(updatedDate)
      setMonthIndex(selectedMonth.monthIndex + 1)
    }
  }

  const days = useMemo(
    () => selectedMonth.createMonthDays((state.content as ConferenceData).conferenceData),
    [selectedMonth, selectedYear, state, selectedWeek],
  )

  const goToCurrentWeek = () => {
    const updatedDate = createDate({ date: new Date() })
    if (viewMode === 'month') {
      setSelectedDate(updatedDate)
      setSelectedMonth(createMonth(updatedDate))
      setTimeout(() => {
        scrollToIdWithOffset(`#${SCROLL_TO_ID.conferencePageToday}`, 120)
      })
    } else {
      setSelectedDate(updatedDate)
      updateWeek(updatedDate.date)
      updateMonthIndexIfNeeded(updatedDate.date, selectedMonth.monthIndex, setMonthIndex)
      setTimeout(() => {
        scrollToIdWithOffset(`#${SCROLL_TO_ID.conferencePageToday}`, 120)
      })
    }
  }

  const getCurrentWeekDays = (selectedDate: Date) => {
    const startOfWeek = dayjs(selectedDate).startOf('isoWeek')
    const endOfWeek = dayjs(selectedDate).endOf('isoWeek')

    const currentWeekDays = days.filter((day) => {
      const dayDate = dayjs(day.date)
      return dayDate.isSameOrAfter(startOfWeek) && dayDate.isSameOrBefore(endOfWeek)
    })

    const daysFromPreviousMonth = []
    if (startOfWeek.month() !== selectedDate.getMonth()) {
      const previousMonthDays = createMonth({
        date: new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1),
        locale,
      }).createMonthDays((state.content as ConferenceData).conferenceData)

      const additionalPrevDays = previousMonthDays.filter((day) => {
        const dayDate = dayjs(day.date)
        return dayDate.isSameOrAfter(startOfWeek) && dayDate.isBefore(dayjs(selectedDate).startOf('month'))
      })

      daysFromPreviousMonth.push(...additionalPrevDays)
    }

    const daysFromNextMonth = []
    if (endOfWeek.month() !== selectedDate.getMonth()) {
      const nextMonthDays = createMonth({
        date: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1),
        locale,
      }).createMonthDays((state.content as ConferenceData).conferenceData)

      const additionalNextDays = nextMonthDays.filter((day) => {
        const dayDate = dayjs(day.date)
        return dayDate.isSameOrBefore(endOfWeek) && dayDate.isAfter(dayjs(selectedDate).endOf('month'))
      })

      daysFromNextMonth.push(...additionalNextDays)
    }

    return [...daysFromPreviousMonth, ...currentWeekDays, ...daysFromNextMonth]
  }

  const visibleDays = useMemo(() => {
    if (viewMode === 'week') {
      return getCurrentWeekDays(selectedDate.date)
    } else {
      const monthNumberOfDays = getMonthNumberOfDays(selectedMonth.monthIndex, selectedMonth.year)

      // из за того, что воскресенье в js имеет нулевой индекс
      const firstDayNumber = days[0].dayNumberInWeek === 0 ? 7 : days[0].dayNumberInWeek
      const lastDayNumber =
        days[monthNumberOfDays - 1].dayNumberInWeek === 0 ? 7 : days[days.length - 1].dayNumberInWeek

      const previousMonthDays = createMonth({
        date: new Date(selectedMonth.year, selectedMonth.monthIndex - 1),
        locale,
      }).createMonthDays()
      const nextMonthDays = createMonth({
        date: new Date(selectedMonth.year, selectedMonth.monthIndex + 1),
        locale,
      }).createMonthDays()

      const previousMonthAdditionalDays = previousMonthDays.slice(
        previousMonthDays.length - firstDayNumber + 1,
        previousMonthDays.length,
      )
      const nextMonthAdditionalDays = nextMonthDays.slice(0, lastDayNumber)

      return [...previousMonthAdditionalDays, ...days, ...nextMonthAdditionalDays]
    }
  }, [selectedMonth.year, selectedMonth.monthIndex, selectedYear, state, viewMode, selectedDate.date, selectedWeek])

  const mergeWeekends = () => {
    let filteredDays: DayType[]

    if (viewMode === 'week') {
      filteredDays = getCurrentWeekDays(selectedDate.date)
    } else {
      filteredDays = days
    }
    const mergedDays: DayType[] = []
    let weekendDays: DayType | null = null

    filteredDays.forEach((day) => {
      if (day.dayShort === 'сб' || day.dayShort === 'вс') {
        if (!weekendDays) {
          weekendDays = { ...day }
        } else {
          weekendDays.dayNumber = `${weekendDays.dayNumber}-${day.dayNumber}`
          weekendDays.dayShort = 'Сб-Вс'
        }
      } else {
        if (weekendDays) {
          mergedDays.push(weekendDays)
          weekendDays = null
        }
        mergedDays.push(day)
      }
    })

    if (weekendDays) {
      mergedDays.push(weekendDays)
    }

    return mergedDays
  }

  const setMonthIndex = (index: number) => {
    if (index === -1) {
      const year = selectedYear - 1

      setSelectedYear(year)
      setSelectedMonth(createMonth({ date: new Date(year, 11), locale }))
    } else if (index === 12) {
      const year = selectedYear + 1

      setSelectedYear(year)
      setSelectedMonth(createMonth({ date: new Date(year, 0), locale }))
    } else {
      setSelectedMonth(createMonth({ date: new Date(selectedYear, index), locale }))
    }
  }

  const onClickArrow = (direction: 'right' | 'left') => {
    if (mode === 'years' && direction === 'left') {
      return setSelectedYearsInterval(getYearsInterval(selectedYearsInterval[0] - 10))
    }

    if (mode === 'years' && direction === 'right') {
      return setSelectedYearsInterval(getYearsInterval(selectedYearsInterval[0] + 10))
    }

    if (mode === 'months' && direction === 'left') {
      const year = selectedYear - 1

      if (!selectedYearsInterval.includes(year)) {
        setSelectedYearsInterval(getYearsInterval(year))
      }

      return setSelectedYear(selectedYear - 1)
    }

    if (mode === 'months' && direction === 'right') {
      const year = selectedYear + 1

      if (!selectedYearsInterval.includes(year)) {
        setSelectedYearsInterval(getYearsInterval(year))
      }

      return setSelectedYear(selectedYear + 1)
    }

    if (mode === 'days') {
      const monthIndex = direction === 'left' ? selectedMonth.monthIndex - 1 : selectedMonth.monthIndex + 1

      if (monthIndex === -1) {
        const year = selectedYear - 1

        setSelectedYear(year)

        if (!selectedYearsInterval.includes(year)) {
          setSelectedYearsInterval(getYearsInterval(year))
        }

        return setSelectedMonth(createMonth({ date: new Date(selectedYear - 1, 11), locale }))
      }

      if (monthIndex === 12) {
        const year = selectedYear + 1

        setSelectedYear(year)

        if (!selectedYearsInterval.includes(year)) {
          setSelectedYearsInterval(getYearsInterval(year))
        }

        return setSelectedMonth(createMonth({ date: new Date(year, 0), locale }))
      }

      setSelectedMonth(createMonth({ date: new Date(selectedYear, monthIndex), locale }))
    }
  }

  return {
    state: {
      visibleDays,
      monthNames,
      weekDayNames,
      selectedDate,
      selectedMonth,
      selectedYear,
      mode,
      selectedYearsInterval,
      selectedWeek,
      viewMode,
      onClickArrow,
      setMode,
      setSelectedDate,
      setSelectedMonth,
      setMonthIndex,
      setSelectedYear,
      setViewMode,
      goToPreviousWeekOrMonth,
      goToNextWeekOrMonth,
      goToCurrentWeek,
      mergeWeekends,
    },
  }
}
